import { PROPERTY_ICON } from "#app/lib/constants/property-icons";
import {
  NO_COLOR,
  PICKER_COLORS,
  toColor,
  toGlyphName,
  type GlyphName,
  type Color,
  type EntityGroupItem,
  type IconName,
  type IconSource,
} from "@salestrak/ui";
import { isStageType, type EntityRefValue, type StageType } from "#app/lib/data";
import type { WorkspaceDataState } from "#app/lib/data/workspace-data-provider";
// Type-only: the CRM directory nouns are prototype-local and carry no runtime
// edge into this module, which every entity chip in the app imports.
import { isInvited, isFormerMember } from "#app/lib/role-utils";
import type { Omittable } from "@salestrak/ui";

// Canonical source for entity kind → icon/label/visual resolution. The
// kind-fallback icon literals live ONLY here — never hard-code them elsewhere.

export type EntityKind =
  | "user"
  | "team"
  | "stage"
  | "workflow"
  | "issue"
  | "issueType"
  | "issueSource";

// Fallbacks only — the entity's own `icon{icon,color}` always wins. People
// (users, contacts) render avatars and stages render the pie glyph, so none of
// them needs a fallback.
export const ENTITY_KIND_ICON: Record<
  Exclude<EntityKind, "stage">,
  IconName
> = {
  user: "user",
  team: "users",
  workflow: "workflow",
  issue: "box",
  issueType: "package",
  issueSource: "text-cursor-input",
};

/** The Assignees property glyph on every surface (property rows, filters,
 *  group-by pickers, bulk actions, column pickers). One person in a ring:
 *  assignment is a per-person fact, and the multi-person `users` glyph
 *  belongs to Teams. */
export const ASSIGNEE_ICON: IconName = PROPERTY_ICON.assignee;

/** What a new Workflow wears before anyone picks: the same picker glyph the
 *  server writes as the column default (workflows.service DEFAULT_ICON). The
 *  kind glyph in ENTITY_KIND_ICON stays the fallback for a stored name no
 *  registry knows. */
export const WORKFLOW_DEFAULT_GLYPH: GlyphName = "symbol-account-tree";

export function iconSourceToName(
  icon: IconSource | null | undefined,
): GlyphName | null {
  return typeof icon === "string" ? icon : null;
}

// Structural supersets of the codegen fragments (WorkspaceUser, WorkspaceTeam,
// WorkflowStageFragment, …) which satisfy them structurally — not local duplicates.

export type EntityUserData = Omittable<{
  name?: string | null;
  email?: string | null;
  username?: string | null;
  profileImage?: string | null;
  /** User-chosen avatar color (palette name or the "none" sentinel) — the
   *  initials fallback renders on the solid key-color circle. Photos and the
   *  suspended/invited gray states always win. */
  avatarColor?: string | null;
  memberStatus?: string | null;
}>;

// Greys read as "unset" on an avatar — the deterministic fallback skips them.
const AVATAR_FALLBACK_PALETTE: readonly Color[] = PICKER_COLORS.filter(
  (c) => c !== "grey" && c !== "dark-grey",
);

/**
 * The TRUE initials-avatar color for a user — what everyone else sees: their
 * CHOSEN color when set, else a stable per-user color hashed from the user id
 * — so a board of assignees never collapses into one brand-colored wall, and
 * a given person looks the same to every viewer, in every session. The
 * explicit "none" sentinel (cleared ≠ never-picked) resolves to null = the
 * themed brand treatment. Photos and the suspended/invited gray states still
 * win downstream.
 *
 * Viewer-side rule: a user's OWN avatar renders themed (null) to
 * them everywhere except profile surfaces — that layer lives in
 * EntityVisual/EntityGroupTag, not here; this function is pure.
 */
export function resolveAvatarColor(
  userId: string,
  chosen?: string | null,
): Color | null {
  if (chosen === NO_COLOR) return null;
  const chosenColor = toColor(chosen);
  if (chosenColor) return chosenColor;
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return AVATAR_FALLBACK_PALETTE[hash % AVATAR_FALLBACK_PALETTE.length] ?? null;
}

export type EntityTeamData = Omittable<{
  teamName: string;
  teamKey?: string | null;
  icon?: Omittable<{ icon?: string | null; color?: string | null }> | null;
}>;

export type EntityStageData = Omittable<{
  name?: string | null;
  type?: string | null;
  icon?: Omittable<{ color?: string | null; percentage?: number | null }> | null;
}>;

export type EntityIconData = Omittable<{
  name?: string | null;
  icon?: Omittable<{ icon?: string | null; color?: string | null }> | null;
}>;

// Call sites build these inline as a component prop (`entity={{ kind: "stage",
// id, stage, workflowId }}`), and `workflowId` is routinely a nullable field
// read straight off a fragment — so an absent key and an explicit `undefined`
// are the same entity, and the two members carrying one relax.
export type EntityData =
  | { kind: "user"; id: string; user: EntityUserData | null }
  | { kind: "team"; id: string; team: EntityTeamData }
  | Omittable<{
      kind: "stage";
      id: string;
      stage: EntityStageData;
      workflowId?: string;
    }>
  | { kind: "workflow"; id: string; workflow: EntityIconData }
  | { kind: "issueType"; id: string; issueType: EntityIconData }
  | Omittable<{
      kind: "issue";
      id: string;
      issue: EntityIconData;
      workflowId?: string;
    }>
  | { kind: "issueSource"; id: string; issueSource: EntityIconData };

/**
 * Structural equality for two `EntityData` values.
 *
 * Call sites build the entity inline (`entity={{ kind: "stage", id, stage }}`),
 * so a fresh object arrives every render and reference equality never holds —
 * memoized chips have to compare by value. `EntityData` is a closed union whose
 * payloads are one object of primitives, at most with a nested `icon`, so a
 * two-level walk is exact. It replaces a `JSON.stringify` pair that ran per chip
 * per render (serializing to compare is O(size) with allocation, and orders of
 * magnitude slower than the handful of field reads below).
 */
export function entityDataEqual(
  a: EntityData | null | undefined,
  b: EntityData | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  // The discriminator and id reject almost every mismatch before any payload
  // walk — a chip whose entity changed at all nearly always changed its id.
  if (a.kind !== b.kind || a.id !== b.id) return false;
  if (
    (a as { workflowId?: string }).workflowId !==
    (b as { workflowId?: string }).workflowId
  ) {
    return false;
  }
  return payloadEqual(
    (a as unknown as Record<string, unknown>)[a.kind],
    (b as unknown as Record<string, unknown>)[b.kind],
  );
}

/** One level of object walk, with `icon` (the only nested object) recursed. */
function payloadEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  ) {
    return false;
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const aKeys = Object.keys(aRecord);
  if (aKeys.length !== Object.keys(bRecord).length) return false;
  for (const key of aKeys) {
    const aValue = aRecord[key];
    const bValue = bRecord[key];
    if (aValue === bValue) continue;
    if (
      typeof aValue === "object" &&
      typeof bValue === "object" &&
      aValue !== null &&
      bValue !== null
    ) {
      if (!payloadEqual(aValue, bValue)) return false;
      continue;
    }
    return false;
  }
  return true;
}

const TYPENAME_TO_KIND: Record<string, EntityKind> = {
  User: "user",
  Member: "user",
  Team: "team",
  Stage: "stage",
  Workflow: "workflow",
  Issue: "issue",
  IssueType: "issueType",
  IssueSource: "issueSource",
};

export function entityKindFromTypename(t: string): EntityKind | undefined {
  return TYPENAME_TO_KIND[t];
}

// Assignees are members-only — an assignee ref is always a user.
export function entityFromAssigneeRef(ref: EntityRefValue): EntityData {
  return { kind: "user", id: ref.entityId, user: ref.user ?? null };
}

/**
 * The `EntityData` behind a member-OR-team id — the shape an assignee, a
 * recipient, or any other "who" field on an automation holds, where the same
 * column can carry either kind. Null when neither map knows the id.
 */
export function entityFromMemberOrTeam(
  maps: Pick<WorkspaceDataState, "userMap" | "teamMap">,
  id: string,
): EntityData | null {
  const user = maps.userMap.get(id);
  if (user) return { kind: "user", id, user };
  const team = maps.teamMap.get(id);
  return team ? { kind: "team", id, team } : null;
}

/**
 * That id's display label, or null when it resolves to nothing.
 *
 * Goes through `resolveEntityLabel` rather than reading `.name`, and that is the
 * whole point: a member's `name` is legitimately empty until they onboard — an
 * invited account has only an email — so `.name` alone yields null and every
 * caller then prints its raw fallback. That is how a user id ("2FQDFgCdMNYbmc")
 * reached the automation node captions and sentences. Use this for any surface
 * that WORDS a who-field; they are all supposed to read identically.
 */
export function resolveMemberOrTeamLabel(
  maps: Pick<WorkspaceDataState, "userMap" | "teamMap">,
  id: string,
): string | null {
  const entity = entityFromMemberOrTeam(maps, id);
  return entity ? resolveEntityLabel(entity) : null;
}

export function userRefToEntity(
  user: { userId: string } & EntityUserData,
): EntityData {
  const { userId, ...rest } = user;
  return { kind: "user", id: userId, user: rest };
}

export function teamRefToEntity(
  team: { teamId: string } & EntityTeamData,
): EntityData {
  const { teamId, ...rest } = team;
  return { kind: "team", id: teamId, team: rest };
}

/**
 * The text an avatar derives initials from — the label's name → email →
 * username chain without its "?" sentinel. Null means the entity carries no
 * identity text at all: render the kind glyph fallback, never punctuation.
 */
export function avatarInitialsSource(
  user: EntityUserData | null | undefined,
): string | null {
  return user?.name || user?.email || user?.username || null;
}

export function resolveEntityLabel(e: EntityData): string {
  switch (e.kind) {
    case "user":
      return e.user?.name || e.user?.email || e.user?.username || "?";
    case "team":
      return e.team.teamName || "?";
    case "stage":
      return e.stage.name ?? "";
    case "workflow":
      return e.workflow.name ?? "";
    case "issueType":
      return e.issueType.name ?? "";
    case "issue":
      return e.issue.name?.trim() || e.id;
    case "issueSource":
      return e.issueSource.name ?? "";
  }
}

// A stage has no glyph slot in the entity-group primitive (no
// `ENTITY_KIND_ICON.stage`); don't put stages in a group, render a single one.
export function entityToGroupItem(entity: EntityData): EntityGroupItem {
  const visual = resolveEntityVisual(entity);
  if (visual.type === "avatar") {
    return {
      id: entity.id,
      // The identity text, not the label — the label's "?" sentinel would
      // read as initials. Null routes the slot to the kind glyph below.
      name: avatarInitialsSource(visual.user),
      fallbackIcon: ENTITY_KIND_ICON.user,
      imageUrl: visual.user?.profileImage ?? null,
      avatarColor: resolveAvatarColor(entity.id, visual.user?.avatarColor),
      muted: visual.former,
      invited: visual.invited,
    };
  }
  if (visual.type === "monogram") {
    // Monogram teams keep the solid tile in group stacks — the letter renders
    // in the glyph's place.
    return {
      id: entity.id,
      name: resolveEntityLabel(entity),
      monogram: visual.letter,
      iconColor: visual.color ?? null,
      iconTile: "solid",
    };
  }
  return {
    id: entity.id,
    name: resolveEntityLabel(entity),
    icon: visual.type === "icon" ? visual.icon : null,
    iconColor: visual.type === "icon" ? (visual.color ?? null) : null,
    // A team is avatar-kind: its picked glyph sits on the same solid tile as
    // its monogram so a stack of teams shares one shape. Every other icon
    // kind is a bare identity glyph, as EntityVisual renders it.
    iconTile: entity.kind === "team" ? "solid" : null,
  };
}

// The display name of each stage CATEGORY (`Stage.type`). These head the
// stage picker's sections and the stage manager's groups, so none of them may
// read as a stage name: a workspace seeds Todo/Doing/Done/Canceled, and an
// group heading "Done" over a row "Done" says nothing. Trade-neutral on purpose — a
// roofer, a drywaller and a developer all say "in progress"; none says "won".
const STAGE_TYPE_LABELS: Record<StageType, string> = {
  pending: "Not started",
  inProgress: "In progress",
  done: "Completed",
  canceled: "Canceled",
};

// Returns null for an empty type so callers decide their own empty text. A
// type outside the known set (a legacy row) falls back to its spaced spelling.
export function stageTypeLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  if (isStageType(type)) return STAGE_TYPE_LABELS[type];
  const spaced = type.replace(/([A-Z])/g, " $1");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function memberStatusBadge(status: string | null | undefined): {
  label: string;
  color: "success" | "warning" | "muted";
} {
  const normalized = status?.toLowerCase();
  if (normalized === "active") return { label: "Active", color: "success" };
  if (normalized === "invited") return { label: "Invited", color: "warning" };
  if (!status) return { label: "Unknown", color: "muted" };
  return {
    label: status.charAt(0).toUpperCase() + status.slice(1).toLowerCase(),
    color: "muted",
  };
}

// Flat glyph primitives for render targets that cannot host React components
// (Google Maps SVG pins, canvas). Icon kinds get the kind-fallback icon; a
// stage carries only its color (the pie glyph is React-only); users carry
// neither (they render avatars). Everything React renders through
// EntityTag/EntityVisual/EntityGroupTag instead.
export function getEntityGlyph(e: EntityData): {
  icon: GlyphName | null;
  color: Color | null;
} {
  const visual = resolveEntityVisual(e);
  switch (visual.type) {
    case "icon":
      return { icon: visual.icon, color: visual.color ?? null };
    case "monogram":
      // Letter tiles are React-only; flat targets fall back to the kind glyph.
      return { icon: ENTITY_KIND_ICON.team, color: visual.color ?? null };
    case "stage":
      return { icon: null, color: toColor(visual.stage.icon?.color) ?? null };
    case "avatar":
      return { icon: null, color: null };
  }
}

export type EntityVisual =
  | {
      type: "avatar";
      user: EntityUserData | null;
      label: string;
      // Suspended or left: rendered muted, never offered in a picker.
      former: boolean;
      invited: boolean;
    }
  | { type: "stage"; stage: EntityStageData }
  // An entity that stores no color has none; every reader already spells that
  // `visual.color ?? …`, so an absent key and an explicit `undefined` are the
  // same answer and the member relaxes rather than forcing `toColor()`'s
  // result to be split into "set the key" and "don't".
  | Omittable<{ type: "icon"; icon: GlyphName | null; color?: Color }>
  // A team without a picked glyph: its name's initial on the solid key-color
  // tile (the default treatment).
  | Omittable<{ type: "monogram"; letter: string; color?: Color }>;

// INTERNAL to the entity system — only `components/entity/*` and this file may
// import it. Feature code renders entities through EntityTag/EntityVisual/
// EntityGroupTag, or uses getEntityGlyph for non-React glyph strings.
export function resolveEntityVisual(e: EntityData): EntityVisual {
  switch (e.kind) {
    case "user":
      return {
        type: "avatar",
        user: e.user,
        label: resolveEntityLabel(e),
        former: isFormerMember(e.user),
        invited: isInvited(e.user),
      };
    case "stage":
      return { type: "stage", stage: e.stage };
    case "team": {
      // No stored glyph → letter monogram (team-name initial on the solid
      // tile, the default treatment). A picked glyph still wins. A stored
      // name the registry does not know is treated as no glyph, so the team
      // falls to its monogram rather than the circle Icon substitutes.
      const glyph = toGlyphName(e.team.icon?.icon);
      if (!glyph) {
        const letter = e.team.teamName?.trim().charAt(0);
        // A team with neither glyph nor name wears the kind glyph — never a
        // "?" monogram.
        if (!letter) {
          return {
            type: "icon",
            icon: ENTITY_KIND_ICON.team,
            color: toColor(e.team.icon?.color),
          };
        }
        return {
          type: "monogram",
          letter,
          color: toColor(e.team.icon?.color),
        };
      }
      return {
        type: "icon",
        icon: glyph,
        color: toColor(e.team.icon?.color),
      };
    }
    case "workflow":
      return {
        type: "icon",
        icon: toGlyphName(e.workflow.icon?.icon) ?? ENTITY_KIND_ICON.workflow,
        color: toColor(e.workflow.icon?.color),
      };
    case "issueType":
      // Project types are NON-THEMED: no color, so the glyph renders muted
      // (bare contexts) or on the muted well (tile contexts), and a mounting
      // surface may ink it itself. The stored color is deliberately ignored
      // for display — it still round-trips in the data.
      return {
        type: "icon",
        icon: toGlyphName(e.issueType.icon?.icon) ?? ENTITY_KIND_ICON.issueType,
      };
    case "issue":
      return {
        type: "icon",
        icon: toGlyphName(e.issue.icon?.icon) ?? ENTITY_KIND_ICON.issue,
        color: toColor(e.issue.icon?.color),
      };
    case "issueSource":
      // Sources have no glyph — when no icon is supplied the visual is empty
      // and only the label renders.
      return {
        type: "icon",
        icon: toGlyphName(e.issueSource.icon?.icon) ?? null,
        color: toColor(e.issueSource.icon?.color),
      };
  }
}
